import { CoachElementTokenProvider } from '@efdigital/ll-addon-coaching/components/third-party/cronofy/coach-element-token-provider';
import { CronofyAvailabilityRules } from '@efdigital/ll-addon-coaching/components/third-party/cronofy/elements/availability-rules';
import { CronofyCalendarSync } from '@efdigital/ll-addon-coaching/components/third-party/cronofy/elements/calendar-sync';
import config from '@efdigital/ll-xceed-v2/config/environment';
import type AvailabilityController from '@efdigital/ll-xceed-v2/controllers/coaching/coach/settings/availability';
import { PAGE_WRAPPER_CONTENT } from '@efdigital/ll-xceed-v2/utils/layout';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type {
  AvailabilityRulesAction,
  AvailabilityRulesConfig,
} from 'cronofy-elements';
import BsModal from 'ember-bootstrap/components/bs-modal';
import { modifier } from 'ember-modifier';
import { pageTitle } from 'ember-page-title';

import office365 from '../../../../assets/images/office-365.svg?url';

const cronofyConfig = config['ll-cronofy'];

interface AvailabilitySignature {
  Args: {
    model: unknown;
    controller: AvailabilityController;
  };
}

export default class extends Component<AvailabilitySignature> {
  @tracked hasChanges: boolean | null = null;
  @tracked showLeaveConfirmation = false;
  pendingTransition: Transition | null = null;
  @service declare router: RouterService;

  get isAlreadyLinkedError() {
    return this.args.controller.calendarSyncError === 'already_linked';
  }

  get isFailedSyncError() {
    return this.args.controller.calendarSyncError === 'failed';
  }

  rulesAction = (event: AvailabilityRulesAction) => {
    switch (event.notification.type) {
      case 'availability_rule_edited':
        this.hasChanges = this.hasChanges !== null;
        break;
      case 'availability_rule_saved':
        this.hasChanges = false;
        break;
    }
  };

  get rulesConfig(): AvailabilityRulesConfig {
    return {
      start_time: '6',
      end_time: '21',
      week_start_day: 'monday',
      auto_create_rules: true,
    };
  }

  stayOnPage = () => {
    this.pendingTransition = null;
    this.showLeaveConfirmation = false;
    return true;
  };

  leavePage = () => {
    const transition = this.pendingTransition;
    this.pendingTransition = null;
    this.hasChanges = false;
    this.showLeaveConfirmation = false;
    transition?.retry();
  };

  /** Intercepts route changes so a focus-managed modal can confirm data loss. */
  interceptTransition = modifier(() => {
    const routeWillChange = (transition: Transition) => {
      if (transition.isAborted) {
        return;
      }

      if (this.hasChanges) {
        transition.abort();
        this.pendingTransition = transition;
        this.showLeaveConfirmation = true;
      }
    };

    this.router.on('routeWillChange', routeWillChange);
    return () => {
      this.router.off('routeWillChange', routeWillChange);
    };
  });

  <template>
    {{pageTitle "Availability"}}

    <CoachElementTokenProvider as |cronofyContext|>
      {{#if this.showLeaveConfirmation}}
        <BsModal
          @open={{this.showLeaveConfirmation}}
          @onHide={{this.stayOnPage}}
          as |modal|
        >
          <modal.header>
            <h2 class="modal-title h5">Leave without saving?</h2>
          </modal.header>
          <modal.body>
            <p class="mb-0">You have unsaved working-hours changes. Leaving this
              page will discard them.</p>
          </modal.body>
          <modal.footer>
            <button
              type="button"
              class="btn btn-outline-primary"
              {{on "click" this.stayOnPage}}
            >Stay on this page</button>
            <button
              type="button"
              class="btn btn-danger"
              {{on "click" this.leavePage}}
            >Leave without saving</button>
          </modal.footer>
        </BsModal>
      {{/if}}

      {{#if this.isAlreadyLinkedError}}
        <div class={{PAGE_WRAPPER_CONTENT}}>
          <div
            class="alert alert-danger mt-3"
            role="alert"
            data-test-calendar-sync-error="already_linked"
          >
            This calendar is already connected to another coach account. Connect
            a different calendar account, or contact support if you believe this
            is a mistake.
          </div>
        </div>
      {{else if this.isFailedSyncError}}
        <div class={{PAGE_WRAPPER_CONTENT}}>
          <div
            class="alert alert-danger mt-3"
            role="alert"
            data-test-calendar-sync-error="failed"
          >
            We could not connect your calendar. Please try again. If the problem
            continues, contact support.
          </div>
        </div>
      {{/if}}

      {{#if cronofyContext.accountStatus.hasCronofyAccount}}
        <div class={{PAGE_WRAPPER_CONTENT}} {{this.interceptTransition}}>
          <h3 class="gud-eyebrow pt-3">Working hours</h3>
          <p class="lead">
            Your calendar is connected. Use the grid below to set the hours you
            are available each week. We combine these working hours with your
            synced calendar availability to determine when coachees can book
            time with you.
          </p>
          <CronofyAvailabilityRules
            @cronofyContext={{cronofyContext}}
            @elementConfig={{this.rulesConfig}}
            @onAction={{this.rulesAction}}
          />
          <p class="mt-4 mb-0">
            <LinkTo
              @route="coaching.coach.settings.availability-preview"
              class="btn btn-outline-primary"
            >
              See how this looks to coachees
              <iconify-icon icon="ph:arrow-right" inline aria-hidden="true" />
            </LinkTo>
          </p>
        </div>

        <hr class="mb-5" />
      {{/if}}
      <div class={{PAGE_WRAPPER_CONTENT}}>
        <h3 class="gud-eyebrow">Calendar syncing</h3>

        {{#if cronofyContext.accountStatus.hasCronofyAccount}}
          <p class="lead">
            Your calendar(s) are synced with Xceed.
          </p>
        {{else}}
          <p class="lead">
            In order to manage your availability and let coachees book sessions
            with you, please connect your calendars to Xceed.
          </p>
        {{/if}}
        <ul class="mb-4">
          <li>This allows us to view and synchronize your availability for
            scheduling.</li>
          <li>We will not access your calendar(s) without your permission, and
            cannot see any details other than your availability.</li>
          <li>When a coach books a session, we will create a new event for the
            session in your default calendar.</li>
        </ul>

        <p>
          Connect your
          <strong>Hult Ashridge</strong>
          work calendar first, using the
          <img src={{office365}} alt="" width="24" /><strong>Office 365</strong>
          option (not Outlook or Exchange) and if prompted log in via Microsoft
          using your EF username. You can then connect additional personal or
          professional calendars so your availability reflects your full
          schedule.
        </p>

        <div class="card border shadow-sm rounded-4">
          <div class="card-body p-4 p-lg-5">
            <CronofyCalendarSync
              @cronofyContext={{cronofyContext}}
              @cronofyConfig={{cronofyConfig}}
            />
          </div>
        </div>
      </div>
    </CoachElementTokenProvider>
  </template>
}
